import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Clock, Car, Euro, FileText, Play, ArrowRight, Sparkles,
  Settings, GripVertical, ChevronUp, ChevronDown
} from 'lucide-react';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, BarChart, Bar
} from 'recharts';

const DEFAULT_WIDGETS = {
  stats: { enabled: true, order: 0 },
  revenueChart: { enabled: true, order: 1 },
  hoursChart: { enabled: true, order: 2 },
  recentTime: { enabled: true, order: 3 },
  recentTrips: { enabled: true, order: 4 },
  openInvoices: { enabled: true, order: 5 },
  projectProgress: { enabled: true, order: 6 },
  quickTimer: { enabled: true, order: 7 },
  calendar: { enabled: true, order: 8 }
};

const WIDGET_ORDER = ['stats', 'revenueChart', 'hoursChart', 'openInvoices', 'projectProgress', 'recentTime', 'recentTrips', 'quickTimer', 'calendar'];

export default function DashboardPage() {
  const { api, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [dashboard, setDashboard] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [projectsData, setProjectsData] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS);
  const [widgetOrder, setWidgetOrder] = useState(WIDGET_ORDER);
  const [isEditing, setIsEditing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showRearrange, setShowRearrange] = useState(false);

  useEffect(() => {
    loadDashboardConfig();
    fetchAllData();
  }, []);

  const loadDashboardConfig = async () => {
    try {
      const res = await api.get('/dashboard/config');
      if (res.data?.widgets) {
        setWidgets(prev => ({ ...prev, ...res.data.widgets }));
      }
      if (res.data?.widgetOrder) {
        setWidgetOrder(res.data.widgetOrder);
      }
    } catch (err) {
      console.log('Using default dashboard config');
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [dashRes, chartRes, projectsRes, calendarRes] = await Promise.all([
        api.get('/dashboard?period=month'),
        api.get('/reports/chart-data?period=month'),
        api.get('/dashboard/projects-summary'),
        api.get('/dashboard/calendar')
      ]);
      setDashboard(dashRes.data);
      setChartData(chartRes.data.data || []);
      setProjectsData(projectsRes.data || []);
      setCalendarData(calendarRes.data || []);
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async (newWidgets, newOrder = widgetOrder) => {
    try {
      await api.put('/dashboard/config', { widgets: newWidgets, widgetOrder: newOrder });
      toast.success(t('settings.saved'));
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  const toggleWidget = (widgetId, enabled) => {
    const newWidgets = { ...widgets, [widgetId]: { ...widgets[widgetId], enabled } };
    setWidgets(newWidgets);
    saveConfig(newWidgets);
  };

  const moveWidget = (index, direction) => {
    const newOrder = [...widgetOrder];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newOrder.length) return;
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    setWidgetOrder(newOrder);
    saveConfig(widgets, newOrder);
  };

  const resetWidgets = () => {
    setWidgets(DEFAULT_WIDGETS);
    setWidgetOrder(WIDGET_ORDER);
    saveConfig(DEFAULT_WIDGETS, WIDGET_ORDER);
    toast.success(t('settings.saved'));
  };

  const periodLabels = {
    week: t('dashboard.thisWeek'),
    month: t('dashboard.thisMonth'),
    quarter: t('dashboard.thisQuarter'),
    year: t('dashboard.thisYear')
  };

  if (loading && !dashboard) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="h-32 bg-muted rounded-xl" />
          <div className="h-32 bg-muted rounded-xl" />
          <div className="h-32 bg-muted rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      {/* Trial Banner */}
      {dashboard?.is_trial && (
        <div className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6" />
            <div>
              <p className="font-semibold">{t('dashboard.freeTrial')}</p>
              <p className="text-sm opacity-90">{t('dashboard.trialDaysLeft', { days: dashboard.trial_days_left })}</p>
            </div>
          </div>
          <Button asChild variant="secondary" className="bg-white text-cyan-600 hover:bg-cyan-50">
            <Link to="/abonnementen">{t('dashboard.viewPlans')}</Link>
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-foreground">
            {t('dashboard.welcome')}{user?.bedrijfsnaam ? ', ' + user.bedrijfsnaam : ''}!
          </h1>
          <p className="text-muted-foreground mt-1">{t('dashboard.overview')} {periodLabels.month}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettings(true)}
            data-testid="edit-dashboard-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            {t('dashboard.widgets')}
          </Button>
        </div>
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {widgetOrder.map((widgetId) => {
          if (!widgets[widgetId]?.enabled) return null;
          
          switch (widgetId) {
            case 'stats':
              return (
                <div key={widgetId} data-testid="widget-stats">
                  <WidgetWrapper title={t('dashboard.statistics')}>
                    <div className="flex flex-col justify-center h-full gap-3">
                      <StatCard icon={<Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />} label={t('dashboard.workedHours')} value={formatNumber(dashboard?.period_hours || 0, 1)} color="blue" />
                      <StatCard icon={<Car className="w-5 h-5 text-green-600 dark:text-green-400" />} label={t('dashboard.drivenKilometers')} value={formatNumber(dashboard?.period_kilometers || 0, 0)} color="green" />
                      <StatCard icon={<Euro className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />} label={t('dashboard.revenue')} value={formatCurrency(dashboard?.period_revenue || 0)} color="cyan" />
                    </div>
                  </WidgetWrapper>
                </div>
              );
            
            case 'revenueChart':
              return (
                <div key={widgetId} data-testid="widget-revenue-chart">
                  <WidgetWrapper title={t('reports.chartRevenue')}>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorOmzet" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} className="text-muted-foreground" stroke="currentColor" />
                          <YAxis tick={{ fontSize: 10 }} className="text-muted-foreground" stroke="currentColor" tickFormatter={(v) => `€${v}`} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))', 
                              border: '1px solid hsl(var(--border))', 
                              borderRadius: '8px',
                              color: 'hsl(var(--card-foreground))'
                            }} 
                            labelStyle={{ color: 'hsl(var(--card-foreground))' }}
                            formatter={(value) => [`€${value.toFixed(2)}`, t('dashboard.revenue')]} 
                          />
                          <Area type="monotone" dataKey="omzet" stroke="#06b6d4" strokeWidth={2} fillOpacity={1} fill="url(#colorOmzet)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </WidgetWrapper>
                </div>
              );
            
            case 'hoursChart':
              return (
                <div key={widgetId} data-testid="widget-hours-chart">
                  <WidgetWrapper title={t('reports.chartHoursKm')}>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} className="text-muted-foreground" stroke="currentColor" />
                          <YAxis yAxisId="left" tick={{ fontSize: 10 }} className="text-muted-foreground" stroke="currentColor" />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} className="text-muted-foreground" stroke="currentColor" />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))', 
                              border: '1px solid hsl(var(--border))', 
                              borderRadius: '8px',
                              color: 'hsl(var(--card-foreground))'
                            }} 
                            labelStyle={{ color: 'hsl(var(--card-foreground))' }}
                          />
                          <Bar yAxisId="left" dataKey="uren" fill="#3b82f6" radius={[4, 4, 0, 0]} name={t('common.hours')} />
                          <Bar yAxisId="right" dataKey="km" fill="#22c55e" radius={[4, 4, 0, 0]} name={t('common.kilometers')} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </WidgetWrapper>
                </div>
              );
            
            case 'openInvoices':
              return (
                <div key={widgetId} data-testid="widget-open-invoices">
                  <WidgetWrapper 
                    title={t('dashboard.openInvoices')} 
                    action={
                      <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-cyan-500 hover:text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-950">
                        <Link to="/facturen" className="flex items-center gap-1">
                          {t('dashboard.viewAll')} <ArrowRight className="w-3 h-3" />
                        </Link>
                      </Button>
                    }
                  >
                    <div className="flex items-center gap-4 h-48 justify-center">
                      <div className="w-14 h-14 bg-amber-100 dark:bg-amber-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <FileText className="w-7 h-7 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-foreground">{dashboard?.open_invoices_count || 0}</p>
                        <p className="text-sm text-muted-foreground">{t('dashboard.invoicesOpen')}</p>
                        <p className="text-lg font-semibold text-amber-600 dark:text-amber-400">{formatCurrency(dashboard?.open_invoices_value || 0)}</p>
                      </div>
                    </div>
                  </WidgetWrapper>
                </div>
              );
            
            case 'projectProgress':
              return (
                <div key={widgetId} data-testid="widget-project-progress">
                  <WidgetWrapper 
                    title={t('dashboard.projectProgress')} 
                    action={
                      <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-cyan-500 hover:text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-950">
                        <Link to="/projecten" className="flex items-center gap-1">
                          {t('dashboard.viewAll')} <ArrowRight className="w-3 h-3" />
                        </Link>
                      </Button>
                    }
                  >
                    <div className="space-y-2 h-48 overflow-auto">
                      {projectsData.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">{t('projects.noProjects')}</p>
                      ) : (
                        projectsData.slice(0, 4).map(project => (
                          <div key={project.project_id} className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate text-foreground">{project.naam}</p>
                              <p className="text-xs text-muted-foreground">{project.client_naam}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${Math.min(project.percentage, 100)}%` }} />
                              </div>
                              <span className="text-xs font-medium w-10 text-right text-foreground">{project.percentage}%</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </WidgetWrapper>
                </div>
              );
            
            case 'recentTime':
              return (
                <div key={widgetId} data-testid="widget-recent-time">
                  <WidgetWrapper 
                    title={t('dashboard.recentTimeEntries')} 
                    action={
                      <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-cyan-500 hover:text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-950">
                        <Link to="/registraties" className="flex items-center gap-1">
                          {t('dashboard.viewAll')} <ArrowRight className="w-3 h-3" />
                        </Link>
                      </Button>
                    }
                  >
                    <div className="space-y-2 h-48 overflow-auto">
                      {(dashboard?.recent_time_entries || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">{t('dashboard.noTimeEntries')}</p>
                      ) : (
                        (dashboard?.recent_time_entries || []).slice(0, 5).map((entry, idx) => (
                          <div key={idx} className="flex items-center justify-between py-1 border-b border-border last:border-0">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate text-foreground">{entry.client_naam}</p>
                              <p className="text-xs text-muted-foreground">{entry.datum}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-foreground">{formatNumber(entry.uren, 2)}u</p>
                              <p className="text-xs text-muted-foreground">{formatCurrency(entry.totaal)}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </WidgetWrapper>
                </div>
              );
            
            case 'recentTrips':
              return (
                <div key={widgetId} data-testid="widget-recent-trips">
                  <WidgetWrapper 
                    title={t('dashboard.recentTrips')} 
                    action={
                      <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-cyan-500 hover:text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-950">
                        <Link to="/registraties" className="flex items-center gap-1">
                          {t('dashboard.viewAll')} <ArrowRight className="w-3 h-3" />
                        </Link>
                      </Button>
                    }
                  >
                    <div className="space-y-2 h-48 overflow-auto">
                      {(dashboard?.recent_mileage_entries || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">{t('dashboard.noTrips')}</p>
                      ) : (
                        (dashboard?.recent_mileage_entries || []).slice(0, 5).map((entry, idx) => (
                          <div key={idx} className="flex items-center justify-between py-1 border-b border-border last:border-0">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate text-foreground">{entry.client_naam}</p>
                              <p className="text-xs text-muted-foreground">{entry.datum}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-foreground">{formatNumber(entry.kilometers, 0)} km</p>
                              <p className="text-xs text-muted-foreground">{formatCurrency(entry.totaal)}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </WidgetWrapper>
                </div>
              );
            
            case 'quickTimer':
              return (
                <div key={widgetId} data-testid="widget-quick-timer">
                  <WidgetWrapper title={t('dashboard.startTimer')}>
                    <div className="flex flex-col items-center justify-center h-48 gap-3">
                      <div className="w-16 h-16 bg-cyan-100 dark:bg-cyan-900/50 rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-cyan-600 dark:text-cyan-400" />
                      </div>
                      <Button onClick={() => navigate('/timer')} className="bg-cyan-500 hover:bg-cyan-600">
                        <Clock className="w-4 h-4 mr-2" />
                        {t('dashboard.startTimer')}
                      </Button>
                    </div>
                  </WidgetWrapper>
                </div>
              );
            
            case 'calendar':
              return (
                <div key={widgetId} data-testid="widget-calendar">
                  <WidgetWrapper title={t('dashboard.calendar')}>
                    <div className="grid grid-cols-7 gap-1 h-48 overflow-auto content-start">
                      {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(day => (
                        <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">{day}</div>
                      ))}
                      {generateCalendarDays(calendarData).map((day, idx) => (
                        <div key={idx} className={`text-center text-xs p-1 rounded ${day.isToday ? 'bg-cyan-500 text-white' : day.hours > 0 ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300' : day.isCurrentMonth ? 'text-foreground' : 'text-muted-foreground/50'}`}>
                          <span>{day.day}</span>
                          {day.hours > 0 && <span className="block text-[10px]">{day.hours}u</span>}
                        </div>
                      ))}
                    </div>
                  </WidgetWrapper>
                </div>
              );
            
            default:
              return null;
          }
        })}
      </div>

      {/* Widget Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('dashboard.manageWidgets')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {Object.keys(DEFAULT_WIDGETS).map((id) => (
              <div key={id} className="flex items-center justify-between">
                <Label htmlFor={`widget-${id}`} className="text-foreground">{getWidgetLabel(id, t)}</Label>
                <Switch id={`widget-${id}`} checked={widgets[id]?.enabled} onCheckedChange={(v) => toggleWidget(id, v)} />
              </div>
            ))}
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between">
            <Button variant="outline" onClick={() => { setShowSettings(false); setShowRearrange(true); }}>
              <GripVertical className="w-4 h-4 mr-2" />
              {t('dashboard.rearrangeWidgets')}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={resetWidgets}>{t('dashboard.resetLayout')}</Button>
              <Button onClick={() => setShowSettings(false)}>{t('common.close')}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Widget Rearrange Dialog */}
      <Dialog open={showRearrange} onOpenChange={setShowRearrange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('dashboard.rearrangeWidgets')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">{t('dashboard.rearrangeDescription')}</p>
          <div className="space-y-2 max-h-[400px] overflow-auto">
            {widgetOrder.map((widgetId, index) => (
              <div 
                key={widgetId} 
                className={`flex items-center justify-between p-3 rounded-lg border ${widgets[widgetId]?.enabled ? 'bg-card border-border' : 'bg-muted/50 border-muted opacity-60'}`}
              >
                <div className="flex items-center gap-3">
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">{getWidgetLabel(widgetId, t)}</span>
                  {!widgets[widgetId]?.enabled && (
                    <span className="text-xs text-muted-foreground">(uitgeschakeld)</span>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => moveWidget(index, 'up')}
                    disabled={index === 0}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => moveWidget(index, 'down')}
                    disabled={index === widgetOrder.length - 1}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={resetWidgets}>{t('dashboard.resetLayout')}</Button>
            <Button onClick={() => setShowRearrange(false)}>{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WidgetWrapper({ title, children, action }) {
  return (
    <Card className="h-full border-0 shadow-sm overflow-hidden">
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {action}
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {children}
      </CardContent>
    </Card>
  );
}

function StatCard({ icon, label, value, color }) {
  const bgColors = {
    blue: 'bg-blue-100 dark:bg-blue-900',
    green: 'bg-green-100 dark:bg-green-900',
    cyan: 'bg-cyan-100 dark:bg-cyan-900'
  };
  return (
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 ${bgColors[color]} rounded-xl flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        <p className="text-lg font-bold truncate">{value}</p>
      </div>
    </div>
  );
}

function getWidgetLabel(id, t) {
  const labels = {
    stats: t('dashboard.statistics'),
    revenueChart: t('reports.chartRevenue'),
    hoursChart: t('reports.chartHoursKm'),
    openInvoices: t('dashboard.openInvoices'),
    projectProgress: t('dashboard.projectProgress'),
    recentTime: t('dashboard.recentTimeEntries'),
    recentTrips: t('dashboard.recentTrips'),
    quickTimer: t('dashboard.startTimer'),
    calendar: t('dashboard.calendar')
  };
  return labels[id] || id;
}

function generateCalendarDays(calendarData) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const days = [];
  
  const hoursMap = {};
  calendarData.forEach(d => {
    hoursMap[d.datum] = d.hours;
  });
  
  // Previous month days
  for (let i = startOffset - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ day: d.getDate(), isCurrentMonth: false, hours: 0, isToday: false });
  }
  
  // Current month days
  for (let i = 1; i <= lastDay.getDate(); i++) {
    const dateStr = `${String(i).padStart(2, '0')}-${String(month + 1).padStart(2, '0')}-${year}`;
    days.push({
      day: i,
      isCurrentMonth: true,
      hours: hoursMap[dateStr] || 0,
      isToday: i === now.getDate()
    });
  }
  
  // Next month days
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ day: i, isCurrentMonth: false, hours: 0, isToday: false });
  }
  
  return days;
}
